import { useEffect, useState, type ChangeEvent } from "react";
import { ApiError, setTouringIdentity, suggestTouringIdentity } from "@/lib/api";
import type { Service, Tour } from "@/lib/types";
import { VocabularyInlineAddDialog } from "@/components/VocabularyInlineAddDialog";

/**
 * TouringIdentityCell — per-membership touring-identity tag editor (U5).
 *
 * Extracted from routes/AdminLoadIn.tsx (2026-05-27). Only rendered when the
 * event has tour_id != null (one-off events use service_location_label and
 * never see this column).
 *
 * Behavior:
 *   - Options come from the tour vocabulary, rendered as `label · tier` so
 *     tier is visible at pick time.
 *   - When the membership has no tag AND has a service_name, fire a one-shot
 *     suggest call. If the API returns a label that matches a vocab entry,
 *     pre-fill the select + show a "(suggested)" annotation adjacent.
 *   - Selecting a vocab entry fires PATCH; on success, clears the suggested
 *     annotation (operator committed a deliberate choice).
 *   - The "+ Add new identity…" sentinel opens VocabularyInlineAddDialog;
 *     on success we refresh the tour vocab and persist the new tag.
 *   - PATCH 400 `identity_not_in_tour_vocabulary` (race with vocab edit)
 *     surfaces as a toast + reverts the select.
 */

const ADD_NEW_SENTINEL = "__ADD_NEW__";

export function TouringIdentityCell({
  tourId, tour, service, onTagged, onVocabRefresh,
}: {
  tourId: string;
  tour: Tour | null;
  service: Service;
  onTagged: (identity: string | null) => void;
  onVocabRefresh: () => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Pre-fill from the suggest endpoint. Stays set until the operator commits
  // a choice — at which point we clear it so the "(suggested)" annotation
  // disappears.
  const [suggested, setSuggested] = useState<string | null>(null);
  // Pending-suggest visibility: when the operator hasn't tagged yet AND the
  // suggest endpoint returned a label that matches a vocab entry, we
  // pre-select that label in the dropdown so the operator can confirm with
  // a single click (matching the existing PATCH; selecting the same value
  // a second time is a no-op).
  const vocab = tour?.touring_service_vocabulary ?? [];
  const currentValue = service.touring_identity ?? suggested ?? "";

  // One-shot suggest fetch — only when this row needs tagging AND has a
  // service_name to look up. We re-fire when service_name or tour_id
  // changes (operator just typed a name in another row, then this row's
  // name changed, etc.) so the suggestion stays accurate.
  const serviceName = service.service_name;
  const currentIdentity = service.touring_identity ?? null;
  useEffect(() => {
    if (currentIdentity != null) {
      setSuggested(null);
      return;
    }
    if (!serviceName) {
      setSuggested(null);
      return;
    }
    let cancelled = false;
    suggestTouringIdentity(tourId, serviceName)
      .then((r) => {
        if (cancelled) return;
        const label = r.touring_identity;
        // Only honor the suggestion when it matches a live vocab entry —
        // otherwise the operator would see a pre-filled option that the
        // dropdown can't actually render.
        if (label && vocab.some((v) => v.label === label)) {
          setSuggested(label);
        } else {
          setSuggested(null);
        }
      })
      .catch(() => { if (!cancelled) setSuggested(null); });
    return () => { cancelled = true; };
    // vocab intentionally omitted from deps — refetching every time the
    // tour reloads would thrash. The suggestion is stable per service_name.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId, serviceName, currentIdentity]);

  const commitTag = async (label: string | null) => {
    setSaving(true);
    try {
      await setTouringIdentity(service.membership_id, label);
      onTagged(label);
      // Operator committed; the suggestion is no longer informative.
      setSuggested(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        const body = e.body as { error?: string } | undefined;
        if (body?.error === "identity_not_in_tour_vocabulary") {
          alert(
            "That identity is no longer in the tour vocabulary — refresh and pick again.",
          );
          // Force a vocab reload so the dropdown options re-render with
          // the current truth.
          void onVocabRefresh();
          return;
        }
        alert(`Couldn't set touring identity: ${body?.error ?? "validation failed"}`);
        return;
      }
      alert(`Failed to set touring identity: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === ADD_NEW_SENTINEL) {
      setDialogOpen(true);
      return;
    }
    void commitTag(v === "" ? null : v);
  };

  const showSuggested = currentIdentity == null && suggested != null;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <select
          value={currentValue}
          disabled={saving}
          onChange={handleChange}
          className="h-8 px-2 text-sm rounded bg-background border border-input w-full"
        >
          <option value="">(none — needs tagging)</option>
          {vocab.map((entry) => (
            <option key={entry.label} value={entry.label}>
              {entry.label} · {entry.tier}
            </option>
          ))}
          {/* If the membership's current tag is no longer in the vocab
              (operator removed the entry concurrently), surface it so the
              operator can see + correct it. */}
          {currentIdentity != null
            && !vocab.some((v) => v.label === currentIdentity) && (
            <option value={currentIdentity}>
              {currentIdentity} (removed)
            </option>
          )}
          <option value={ADD_NEW_SENTINEL}>+ Add new identity to tour…</option>
        </select>
        {showSuggested && (
          <span
            className="text-[10px] text-muted-foreground whitespace-nowrap"
            title="Pre-filled from the most-recent tagging on this tour. Confirm or change."
          >
            (suggested)
          </span>
        )}
      </div>

      {dialogOpen && (
        <VocabularyInlineAddDialog
          tourId={tourId}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onAdded={async (entry) => {
            // Refresh the vocab so the new entry shows up as an option,
            // THEN persist the tag on this membership. The order matters
            // — if commitTag landed first against a pre-refresh vocab,
            // the server would still accept it (the entry just got
            // added), but the dropdown would briefly render the new
            // label as "(removed)" until the refresh completes.
            await onVocabRefresh();
            void commitTag(entry.label);
          }}
        />
      )}
    </>
  );
}
